/**
 * Admin products API module — CON-PRODUCT-001.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/products          — paginated list
 *   GET    /api/v1/admin/products/:id      — single product
 *   POST   /api/v1/admin/products          — create product
 *   PUT    /api/v1/admin/products/:id      — update product
 *   DELETE /api/v1/admin/products/:id      — delete product
 *   POST   /api/v1/admin/products/:id/publish    — publish product
 *   POST   /api/v1/admin/products/:id/unpublish  — unpublish product
 *
 * Snake→camel projection for pagination lives here, not in the slice.
 * Per memory `project_snake_camel_wire_shape_gap`, the platform emits
 * `meta.pagination` with snake_case keys; this module maps them to the
 * camelCase `AdminProductsListResponse` shape the slice consumes.
 *
 * Backend dependency: PL-CON-PRODUCT-002 / PL-CON-PRODUCT-003 / PL-CON-PRODUCT-004.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminProductCreatePayload,
  AdminProductDTO,
  AdminProductFormOptionsDTO,
  AdminProductsListParams,
  AdminProductsListResponse,
  AdminProductUpdatePayload,
} from '@dfq/shared/types/product';

/** Snake_case pagination emitted by `meta.pagination` in the platform envelope. */
interface WireProductPaginationMeta {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
}

export async function fetchAdminProducts(
  params: AdminProductsListParams = {},
): Promise<AdminProductsListResponse> {
  try {
    const queryParams: Record<string, string | number | undefined> = {
      page: params.page,
      per_page: params.perPage,
      sort: params.sort,
      direction: params.direction,
      status: params.status,
      search: params.search,
    };

    const res = await apiClient.get<
      ApiEnvelope<{ products: AdminProductDTO[] }> & {
        meta: { pagination?: WireProductPaginationMeta };
      }
    >('/admin/products', { params: queryParams });

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      products: data.products,
      total: pagination?.total ?? 0,
      page: pagination?.page ?? 1,
      perPage: pagination?.per_page ?? 25,
      lastPage: pagination?.last_page ?? 1,
    } satisfies AdminProductsListResponse;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminProduct(id: number): Promise<AdminProductDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ product: AdminProductDTO }>>(
      `/admin/products/${id}`,
    );
    return unwrap(res.data, res.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function createAdminProduct(
  payload: AdminProductCreatePayload,
): Promise<AdminProductDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ product: AdminProductDTO }>>(
      '/admin/products',
      payload,
    );
    return unwrap(res.data, res.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function updateAdminProduct(
  id: number,
  payload: AdminProductUpdatePayload,
): Promise<AdminProductDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ product: AdminProductDTO }>>(
      `/admin/products/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function deleteAdminProduct(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/products/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function publishAdminProduct(id: number): Promise<AdminProductDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ product: AdminProductDTO }>>(
      `/admin/products/${id}/publish`,
    );
    return unwrap(res.data, res.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function unpublishAdminProduct(id: number): Promise<AdminProductDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ product: AdminProductDTO }>>(
      `/admin/products/${id}/unpublish`,
    );
    return unwrap(res.data, res.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Fetch the enum value sets the product create/edit form needs
 * (`productType`, `sizingMode`, `pricingUnit`) — sourced from the platform's
 * code enums so the console never hardcodes them. CON-PROD-101.
 *
 * Route: `GET /api/v1/admin/products/options`
 * Auth:  `auth:console`, scope `catalog:admin`
 */
export async function getAdminProductOptions(): Promise<AdminProductFormOptionsDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ options: AdminProductFormOptionsDTO }>>(
      '/admin/products/options',
    );
    return unwrap(res.data, res.status).options;
  } catch (err) {
    throwAsApiError(err);
  }
}
